import { Ionicons, MaterialIcons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useFonts } from 'expo-font';
import * as Linking from 'expo-linking';
import { Slot, useSegments } from 'expo-router';
import { Drawer } from 'expo-router/drawer';
import { StatusBar } from 'expo-status-bar';
import React, { useEffect, useState } from 'react';
import { ActivityIndicator, Platform, View } from 'react-native';
import { GestureHandlerRootView } from 'react-native-gesture-handler';

import SignIn from './auth';
import AuthErrorPage from './auth-error';

import { AlertProvider } from '@/components/alert-provider';
import { SessionProvider, useSession } from '@/components/auth';
import { AuthErrorHost } from '@/components/auth-error-host';
import { EnvironmentBanner } from '@/components/common/environment-banner';
import { OfflineBanner } from '@/components/common/offline-banner';
import { ComposerProvider } from '@/components/composers/composer-host';
import { ApprovalOverdueBanner } from '@/components/dashboards/approval-overdue-banner';
import { DataProvider } from '@/components/data-provider';
import { ErrorBoundary } from '@/components/error-boundary';
import { GlobalSearchPopup } from '@/components/global-search-popup';
import { ImpersonationBanner } from '@/components/impersonation-banner';
import CustomDrawerContent from '@/components/layout/custom-drawer-content';
import { NotificationProvider as PushNotificationProvider } from '@/components/notification-provider';
import { NotificationProvider } from '@/components/notification-system';
import { ATTConsentGate } from '@/components/privacy/att-consent-gate';
import { ThemeProvider } from '@/components/theme-provider';
import { Tracker } from '@/components/tracker';
import { useDevSignInLink } from '@/hooks/use-dev-sign-in-link';
import { useIsLargeScreen } from '@/hooks/use-is-large-screen';
import { useIsDesktopWeb } from '@/hooks/use-is-mobile-web';
import { useBackgroundRefresh } from '@/hooks/use-live-refresh';
import { SearchProvider } from '@/hooks/use-search';
import { DEVICE_TOKEN_KEY } from '@/services/device-token-service';
import { secureStoreService } from '@/services/secure-store-service';
import { SecureUnlockProvider } from '@/services/secure-unlock-context';
import { isDevBuild } from '@/utils/dev-sign-in';
import { ROOT_DRAWER_BACK_BEHAVIOR } from '@/utils/drawer-section';
import { useTrackInAppNavigation } from '@/utils/router-back';


/**
 * DEV-ONLY deep-link login, for driving a simulator/device build against a local
 * API without going through an OAuth provider (see `.maestro/auth.yml`):
 *
 *   xcrun simctl openurl booted \
 *     "workorder://auth/dev-login?token=<64-hex device token>&email=…&name=…&id=…"
 *
 * Stripped from release builds by `__DEV__`. The token must already exist in the
 * target API's `device_tokens` table — this seeds the client, it does not mint
 * credentials.
 *
 * Writes the same three keys the OAuth path persists, through `secureStoreService`
 * (Keychain on native, localStorage on web). Previously this wrote a lone
 * `authToken` key that nothing ever read, so the link silently did nothing.
 */
Linking.addEventListener('url', async ({ url }) => {
	const { queryParams, path } = Linking.parse(url);
	if (!__DEV__ || path !== 'auth/dev-login' || !queryParams?.token) return;

	const token = String(queryParams.token);
	const id = String(queryParams.id ?? 'dev-user');
	const email = String(queryParams.email ?? 'dev@local');
	const name = String(queryParams.name ?? 'Dev User');

	await secureStoreService.setItemAsync(DEVICE_TOKEN_KEY, token);
	await secureStoreService.setItemAsync('userSession', JSON.stringify({
		id, name, email, provider: 'azure', deviceToken: token,
	}));
	await secureStoreService.setItemAsync('currentUser', JSON.stringify({
		id, email, name, role: String(queryParams.role ?? 'admin'), organizationId: 'primary',
	}));
	// AsyncStorage copy retained for anything still reading the legacy key.
	await AsyncStorage.setItem('authToken', token);
});


const AppLayout: React.FC = () => {
	const isLargeScreen = useIsLargeScreen();
	const isDesktopWeb = useIsDesktopWeb();
	const { session, isLoading, authError, user, isUserLoading } = useSession();
	useDevSignInLink(!session);
	const segments = useSegments();
	const isWeb = Platform.OS === 'web';
	useTrackInAppNavigation();
	/*
		Keep badges and the dashboard fresh while a page sits open.

		Mounted ONCE here rather than as a `refetchInterval` on each hook that feeds a badge:
		the one somebody forgets to add is invisible — the number just goes stale, and nobody
		files a bug about a count that was never wrong, only old. It skips the tick entirely
		while the tab is hidden, and only refetches queries something is actually subscribed
		to. See `LIVE_QUERY_KEYS`.
	*/
	useBackgroundRefresh();

	const [fontTimeout, setFontTimeout] = useState(false);
	// Collapse the permanent (desktop) nav drawer to an icons-only rail.
	const [drawerCollapsed, setDrawerCollapsed] = useState(false);
	const collapsed = isLargeScreen && drawerCollapsed;
	const [fontsLoaded] = useFonts(isWeb ? {
		...Ionicons.font,
		...MaterialIcons.font,
	} : {});

	useEffect(() => {
		if (!isWeb) return;
		const printLink = document.createElement('link');
		printLink.id = 'web-native-print-styles';
		printLink.rel = 'stylesheet';
		printLink.href = '/print-styles.css';
		printLink.type = 'text/css';

		const leafletLink = document.createElement('link');
		leafletLink.id = 'leaflet-styles';
		leafletLink.rel = 'stylesheet';
		leafletLink.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
		leafletLink.type = 'text/css';

		document.head.appendChild(printLink);
		document.head.appendChild(leafletLink);

		return () => {
			if (document.head.contains(printLink)) document.head.removeChild(printLink);
			if (document.head.contains(leafletLink)) document.head.removeChild(leafletLink);
		};
	}, [isWeb]);

	useEffect(() => {
		if (!isWeb) return;
		const timer = setTimeout(() => setFontTimeout(true), 1000);
		return () => clearTimeout(timer);
	}, [isWeb, fontsLoaded]);

	const isPrintRoute = segments[0] === 'print';
	// The only public route: the dev-only device-token sign-in (app/dev/sign-in.tsx), which
	// has to mount BEFORE there is a session — that is its whole job. Never in a release build.
	const isPublicRoute = isDevBuild() && segments[0] === 'dev';

	if (isWeb && !fontsLoaded && !fontTimeout) {
		return (
			<View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
				<ActivityIndicator size="large" />
			</View>
		);
	}

	if (isPublicRoute) {
		return (
			<ThemeProvider>
				<ErrorBoundary>
					<GestureHandlerRootView style={{ flex: 1 }}>
						<Slot />
					</GestureHandlerRootView>
				</ErrorBoundary>
			</ThemeProvider>
		);
	}

	if (!session) {
		return (
			<ThemeProvider>
				<ErrorBoundary>
					<SessionProvider>
						<DataProvider>
							<EnvironmentBanner />
							<OfflineBanner />
							{isLoading ? <ActivityIndicator /> : <SignIn />}
						</DataProvider>
					</SessionProvider>
				</ErrorBoundary>
			</ThemeProvider>
		);
	}

	// The full-page variant only when no user ever loaded (an unprovisioned account's first /me):
	// there is nothing underneath to keep. Once the app is up, `AuthErrorHost` shows the prompt
	// OVER it instead, so a refused session never unmounts unsaved work (#692).
	if (authError && !user) {
		return (
			<ThemeProvider>
				<ErrorBoundary>
					<SessionProvider>
						<DataProvider>
							<EnvironmentBanner />
							<OfflineBanner />
							<AuthErrorPage />
						</DataProvider>
					</SessionProvider>
				</ErrorBoundary>
			</ThemeProvider>
		);
	}

	// After Microsoft OAuth we have a session before /me has resolved. If the
	// account isn't provisioned in the app, /me 401s and we'll route to the
	// AuthErrorPage — but without this gate the Drawer would flash on screen
	// during the gap.
	if (isUserLoading && !user) {
		return (
			<ThemeProvider>
				<ErrorBoundary>
					<SessionProvider>
						<DataProvider>
							<EnvironmentBanner />
							<OfflineBanner />
							<View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
								<ActivityIndicator size="large" />
							</View>
						</DataProvider>
					</SessionProvider>
				</ErrorBoundary>
			</ThemeProvider>
		);
	}

	if (isPrintRoute) {
		return (
			<AlertProvider>
				<NotificationProvider>
					<PushNotificationProvider>
						<SecureUnlockProvider>
							<ErrorBoundary>
								<Tracker />
							</ErrorBoundary>
							<SearchProvider>
								<GestureHandlerRootView style={{ flex: 1 }}>
									<EnvironmentBanner />
									<OfflineBanner />
									{/* Print pages bypass the drawer, so they need their own host or a refused session shows nothing. */}
									<AuthErrorHost>
										<Slot />
									</AuthErrorHost>
								</GestureHandlerRootView>
							</SearchProvider>
						</SecureUnlockProvider>
					</PushNotificationProvider>
				</NotificationProvider>
			</AlertProvider>
		);
	}

	return (
		<AlertProvider>
			{/* `userId` owns the persisted toast history (#633) — see services/notification-history.ts. */}
			<NotificationProvider userId={user?.id ?? null}>
				<PushNotificationProvider>
					<SecureUnlockProvider>
						<ErrorBoundary>
							<Tracker />
						</ErrorBoundary>
						<SearchProvider>
							{/*
								Email composers live HERE, outside the Drawer, so a minimized draft
								survives navigation. Rendered by the page that opened them, they
								unmounted with it — minimize a reply, click Dashboard, letter gone.
							*/}
							<ComposerProvider>
								<GestureHandlerRootView style={{ flex: 1 }}>
									<EnvironmentBanner />
									<OfflineBanner />
									<ImpersonationBanner />
									{/*
									Overdue approvals, portal-wide (#496). Mounted here beside the other
									shell banners rather than on the dashboard, because a property
									manager who arrives from an email link or lives in the inbox all
									morning never sees the dashboard — and missed approvals are the
									problem this feature exists to fix. Self-hides unless the signed-in
									user is a PM with work past its two-business-day window; print
									routes return early above, so this never reaches paper.
								*/}
									<ApprovalOverdueBanner />
									<GlobalSearchPopup />
									{/*
										`fullHistory` makes browser Back work across drawer sections (#651): the drawer router
										pushes a browser entry for a section switch only under `history`/`fullHistory`; the
										default `firstRoute` replaced it, so Back skipped the previous screen. A cross-section
										`router.replace` (route guards, auth redirects) still replaces, so Back never lands on
										the guarded page. Details on `ROOT_DRAWER_BACK_BEHAVIOR`.
									*/}
									<AuthErrorHost>
										<Drawer
											backBehavior={ROOT_DRAWER_BACK_BEHAVIOR}
											drawerContent={(props) => (
												<CustomDrawerContent
													{...props}
													collapsed={collapsed}
													onToggleCollapsed={isLargeScreen ? () => setDrawerCollapsed((v) => !v) : undefined}
												/>
											)}
											screenOptions={isLargeScreen ? {
												drawerType: 'permanent',
												drawerStyle: { width: collapsed ? 72 : 280 },
												drawerPosition: 'left',
												headerShown: false,
											} : {
												drawerType: 'slide',
												drawerStyle: { width: 280 },
												drawerPosition: 'left',
												headerShown: false,
												swipeEnabled: !isDesktopWeb,
											}}
										>
											<Drawer.Screen name="index" options={{ drawerItemStyle: { display: 'none' }, headerShown: false }} />
											<Drawer.Screen name="work-orders" options={{ drawerItemStyle: { display: 'none' }, headerShown: false }} />
											<Drawer.Screen name="inbox" options={{ drawerItemStyle: { display: 'none' }, headerShown: false }} />
											<Drawer.Screen name="exterior-applications" options={{ drawerItemStyle: { display: 'none' }, headerShown: false }} />
											<Drawer.Screen name="quotes" options={{ drawerItemStyle: { display: 'none' }, headerShown: false }} />
											<Drawer.Screen name="pending" options={{ drawerItemStyle: { display: 'none' }, headerShown: false }} />
											<Drawer.Screen name="unassigned" options={{ drawerItemStyle: { display: 'none' }, headerShown: false }} />
											<Drawer.Screen name="tasks" options={{ drawerItemStyle: { display: 'none' }, headerShown: false }} />
											<Drawer.Screen name="associations" options={{ drawerItemStyle: { display: 'none' }, headerShown: false }} />
											<Drawer.Screen name="users" options={{ drawerItemStyle: { display: 'none' }, headerShown: false }} />
											<Drawer.Screen name="materials" options={{ drawerItemStyle: { display: 'none' }, headerShown: false }} />
											<Drawer.Screen name="settings" options={{ drawerItemStyle: { display: 'none' }, headerShown: false }} />
											<Drawer.Screen name="reports" options={{ drawerItemStyle: { display: 'none' }, headerShown: false }} />
											<Drawer.Screen name="accounting" options={{ drawerItemStyle: { display: 'none' }, headerShown: false }} />
											<Drawer.Screen name="admin" options={{ drawerItemStyle: { display: 'none' }, headerShown: false }} />
											<Drawer.Screen name="auth" options={{ drawerItemStyle: { display: 'none' }, headerShown: false }} />
											<Drawer.Screen name="auth-error" options={{ drawerItemStyle: { display: 'none' }, headerShown: false }} />
											<Drawer.Screen name="print" options={{ drawerItemStyle: { display: 'none' }, headerShown: false }} />
											{/*
											EVERY root route must be declared here. SDK 58's drawer redirects a focused route
											the layout did not declare to the initial route, so an undeclared `/documents`
											landed on the Dashboard. Guarded by root-drawer-declares-every-route.spec.ts.
										*/}
											<Drawer.Screen name="todo" options={{ drawerItemStyle: { display: 'none' }, headerShown: false }} />
											<Drawer.Screen name="delegated" options={{ drawerItemStyle: { display: 'none' }, headerShown: false }} />
											<Drawer.Screen name="documents" options={{ drawerItemStyle: { display: 'none' }, headerShown: false }} />
											<Drawer.Screen name="inspections" options={{ drawerItemStyle: { display: 'none' }, headerShown: false }} />
											<Drawer.Screen name="review-requests" options={{ drawerItemStyle: { display: 'none' }, headerShown: false }} />
											<Drawer.Screen name="ownership-transfers" options={{ drawerItemStyle: { display: 'none' }, headerShown: false }} />
											<Drawer.Screen name="oauth/mcp" options={{ drawerItemStyle: { display: 'none' }, headerShown: false }} />
											<Drawer.Screen name="dev/sign-in" options={{ drawerItemStyle: { display: 'none' }, headerShown: false }} />
											<Drawer.Screen name="+not-found" options={{ drawerItemStyle: { display: 'none' }, headerShown: false }} />
										</Drawer>
									</AuthErrorHost>
								</GestureHandlerRootView>
							</ComposerProvider>
						</SearchProvider>
					</SecureUnlockProvider>
				</PushNotificationProvider>
			</NotificationProvider>
		</AlertProvider>
	);

};

const RootLayout: React.FC = () => {
	return (
		<ThemeProvider>
			<SessionProvider>
				<ATTConsentGate />
				<ErrorBoundary>
					<DataProvider>
						<AppLayout />
					</DataProvider>
				</ErrorBoundary>
				<StatusBar style="dark" />
			</SessionProvider>
		</ThemeProvider>
	);
};

export default RootLayout;
